import AddIcon from '@mui/icons-material/Add';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import DownloadIcon from '@mui/icons-material/Download';
import HistoryIcon from '@mui/icons-material/History';
import RemoveIcon from '@mui/icons-material/Remove';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import {
  alpha,
  Alert,
  Box,
  CircularProgress,
  Paper,
  Theme,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import LFCIconButton from '_components/inputs/LFCIconButton';
import {getLanguageByFileName, getSyntaxColors, Token} from '_logics/code-highlight';
import {
  AIExplanationInputTooLargeError,
  downloadSourceFile,
  explainSourceCode
} from '_logics/LFCSourceUtil';
import {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import AiMarkdownViewer from '_components/common/AiMarkdownViewer';
import {useMySettings} from '_contexts/MySettingsProvider';

type SourceViewerProps = {
  content: string | null;
  fileName: string | null;
  filePath: string | null;
  fileSize: number | null;
  downloadUrl: string | null;
  loading: boolean;
  hasHistory?: boolean;
  onOpenHistory?: () => void;
  /** コミット日時（ISO形式） */
  commitDate?: string | null;
};

/**
 * ファイルサイズを整形する
 * @param bytes バイト数
 * @returns 整形後の文字列
 */
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) {
    return bytes === 1 ? '1 Byte' : `${bytes} Bytes`;
  } else if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  } else {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
};

/**
 * 日付をフォーマットする
 */
const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  } catch {
    return dateString;
  }
};

/** コード表示ペイン */
const CodePane = memo(
  ({
    lines,
    highlightedLines,
    renderTokens,
    fontSize,
    isDark,
    theme
  }: {
    lines: string[];
    highlightedLines: Token[][] | null;
    renderTokens: (tokens: Token[]) => React.ReactNode;
    fontSize: number;
    isDark: boolean;
    theme: Theme;
  }) => (
    <Box
      sx={{
        overflow: 'auto',
        flex: 1,
        bgcolor: isDark ? 'grey.900' : 'grey.100',
        minWidth: 0,
        minHeight: 0
      }}
    >
      <pre
        style={{
          margin: 0,
          padding: 0,
          fontFamily: 'monospace',
          fontSize: `${fontSize}px`,
          lineHeight: '1.5',
          minWidth: 0
        }}
      >
        <code>
          {lines.map((line, index) => (
            <Box
              key={index}
              component="div"
              sx={{
                display: 'flex',
                '&:hover': {bgcolor: alpha(theme.palette.primary.main, 0.08)}
              }}
            >
              <Box
                component="span"
                sx={{
                  minWidth: '4em',
                  px: 2,
                  py: 0.25,
                  textAlign: 'right',
                  color: 'text.secondary',
                  bgcolor: isDark ? 'grey.800' : 'grey.200',
                  borderRight: 1,
                  borderColor: 'divider',
                  userSelect: 'none'
                }}
              >
                {index + 1}
              </Box>
              <Box
                component="span"
                sx={{
                  flex: 1,
                  px: 2,
                  py: 0.25,
                  whiteSpace: 'pre',
                  color: 'text.primary'
                }}
              >
                {highlightedLines ? renderTokens(highlightedLines[index]) : line}
              </Box>
            </Box>
          ))}
        </code>
      </pre>
    </Box>
  )
);

/**
 * GitHub風コード表示コンポーネント
 * .jblファイルはMOTOMAN INFORM言語としてシンタックスハイライト
 */
const SourceViewer = ({
  content,
  fileName,
  filePath,
  fileSize,
  downloadUrl,
  loading,
  hasHistory = false,
  onOpenHistory,
  commitDate
}: SourceViewerProps) => {
  const {t} = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const mySettings = useMySettings();

  // フォントサイズ（デフォルト100%=14px、±5%刻み、80%〜120%）
  const DEFAULT_ZOOM = 100;
  const BASE_FONT_SIZE = 14;
  const [zoomPercent, setZoomPercent] = useState(DEFAULT_ZOOM);
  const fontSize = (zoomPercent / 100) * BASE_FONT_SIZE;
  const handleZoomIn = () => setZoomPercent(prev => Math.min(prev + 5, 120));
  const handleZoomOut = () => setZoomPercent(prev => Math.max(prev - 5, 80));
  const handleResetZoom = () => setZoomPercent(DEFAULT_ZOOM);

  // エディタ分割: 'none' | 'horizontal'(左右) | 'vertical'(上下)
  const [editorSplit, setEditorSplit] = useState<'none' | 'horizontal' | 'vertical'>('none');
  const [splitRatio, setSplitRatio] = useState(50); // 1番目ペインの割合(%)
  const splitResizing = useRef(false);
  const editorAreaRef = useRef<HTMLDivElement>(null);

  const handleSplitResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      splitResizing.current = true;
      document.body.style.cursor = editorSplit === 'horizontal' ? 'col-resize' : 'row-resize';
      document.body.style.userSelect = 'none';
    },
    [editorSplit]
  );

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!splitResizing.current || !editorAreaRef.current) return;
      const rect = editorAreaRef.current.getBoundingClientRect();
      let ratio: number;
      if (editorSplit === 'horizontal') {
        ratio = ((e.clientX - rect.left) / rect.width) * 100;
      } else {
        ratio = ((e.clientY - rect.top) / rect.height) * 100;
      }
      setSplitRatio(Math.max(20, Math.min(ratio, 80)));
    };
    const onMouseUp = () => {
      splitResizing.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [editorSplit]);

  // AI解説
  const [aiExplanation, setAiExplanation] = useState<string | null>(null);
  const [aiError, setAiError] = useState<'input-too-large' | 'generic' | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const aiAbortRef = useRef<AbortController | null>(null);

  // AI解説パネルの幅リサイズ（右側固定）
  const [aiWidth, setAiWidth] = useState(400);
  const aiResizing = useRef(false);

  const handleAiResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    aiResizing.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!aiResizing.current) return;
      const paperEl = document.getElementById('source-viewer-paper');
      if (!paperEl) return;
      const paperRect = paperEl.getBoundingClientRect();
      const newWidth = paperRect.right - e.clientX;
      setAiWidth(Math.max(200, Math.min(newWidth, paperRect.width * 0.6)));
    };
    const onMouseUp = () => {
      aiResizing.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  // ファイル切り替え時にAI解説をリセット
  useEffect(() => {
    setAiExplanation(null);
    setAiError(null);
    setAiOpen(false);
    setAiLoading(false);
    aiAbortRef.current?.abort();
  }, [filePath]);

  const handleAiExplain = useCallback(async () => {
    if (!content || !fileName || !filePath) return;

    if (aiOpen && (aiExplanation || aiError)) {
      setAiOpen(false);
      return;
    }

    setAiOpen(true);
    setAiLoading(true);
    setAiExplanation(null);
    setAiError(null);

    aiAbortRef.current?.abort();
    const abortController = new AbortController();
    aiAbortRef.current = abortController;

    try {
      const result = await explainSourceCode(
        filePath,
        fileName,
        content,
        abortController.signal,
        mySettings.locale as 'ja' | 'en' | undefined
      );
      setAiExplanation(result.explanation);
    } catch (error: unknown) {
      if (error instanceof Error && error.name === 'CanceledError') return;
      setAiError(error instanceof AIExplanationInputTooLargeError ? 'input-too-large' : 'generic');
    } finally {
      setAiLoading(false);
    }
  }, [content, fileName, filePath, aiOpen, aiExplanation, aiError, mySettings.locale]);

  // テーマに応じたシンタックスハイライト色を取得
  const syntaxColors = useMemo(() => getSyntaxColors(isDark), [isDark]);

  const handleDownload = () => {
    if (!fileName) return;

    // 大容量ファイルの場合はpresigned URLを使用
    if (downloadUrl) {
      downloadSourceFile(downloadUrl, fileName);
      return;
    }

    // 通常ファイルの場合はcontentからBlobを作成してダウンロード
    if (content) {
      const blob = new Blob([content], {type: 'text/plain;charset=utf-8'});
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  // ファイル名から言語定義を取得
  const language = getLanguageByFileName(fileName);

  // トークンをReact要素に変換
  const renderTokens = useCallback(
    (tokens: Token[]): React.ReactNode => {
      return tokens.map((token, index) => {
        if (token.type === 'text') {
          return <span key={index}>{token.value}</span>;
        }
        const color = syntaxColors[token.type as keyof typeof syntaxColors];
        return (
          <span key={index} style={{color}}>
            {token.value}
          </span>
        );
      });
    },
    [syntaxColors]
  );

  // 行ごとにトークン化（メモ化）
  const highlightedLines = useMemo(() => {
    if (!content || !language) return null;
    return content.split('\n').map(line => language.tokenizeLine(line));
  }, [content, language]);

  const lines = useMemo(() => content?.split('\n') ?? [], [content]);

  const codePaneProps = useMemo(
    () => ({
      lines,
      highlightedLines,
      renderTokens,
      fontSize,
      isDark,
      theme
    }),
    [lines, highlightedLines, renderTokens, fontSize, isDark, theme]
  );

  // ローディング中
  if (loading) {
    return (
      <Paper
        sx={{p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%'}}
      >
        <CircularProgress />
      </Paper>
    );
  }

  // ファイル未選択
  if (content === null) {
    return (
      <Paper
        sx={{p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%'}}
      >
        <Typography variant="body1" color="text.secondary">
          {t('ファイルを選択してください')}
        </Typography>
      </Paper>
    );
  }

  // エディタ分割の切替
  const handleToggleSplit = (mode: 'horizontal' | 'vertical') => {
    setEditorSplit(prev => (prev === mode ? 'none' : mode));
  };

  return (
    <Paper
      id="source-viewer-paper"
      sx={{
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0
      }}
    >
      {/* ヘッダー */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: isDark ? 'grey.800' : 'grey.200'
        }}
      >
        <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0}}>
          <Typography
            variant="h6"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
            title={(filePath || fileName || '').replace(/^devices\//, '')}
          >
            {(filePath || fileName || '').replace(/^devices\//, '')}
          </Typography>
          {commitDate && (
            <Typography variant="body2" color="text.secondary" sx={{flexShrink: 0}}>
              {formatDate(commitDate)}
            </Typography>
          )}
          {fileSize !== null && (
            <Typography variant="body2" color="text.secondary" sx={{flexShrink: 0}}>
              {formatFileSize(fileSize)}
            </Typography>
          )}
          {language && (
            <Typography
              variant="caption"
              sx={{
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                px: 1,
                py: 0.25,
                borderRadius: 1,
                flexShrink: 0
              }}
            >
              {language.badge}
            </Typography>
          )}
        </Box>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
          <Tooltip title={t('上下分割')}>
            <span>
              <LFCIconButton
                color={editorSplit === 'vertical' ? 'secondary' : 'primary'}
                size="small"
                onClick={() => handleToggleSplit('vertical')}
              >
                <ViewStreamIcon />
              </LFCIconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('左右分割')}>
            <span>
              <LFCIconButton
                color={editorSplit === 'horizontal' ? 'secondary' : 'primary'}
                size="small"
                onClick={() => handleToggleSplit('horizontal')}
              >
                <ViewColumnIcon />
              </LFCIconButton>
            </span>
          </Tooltip>
          <Box sx={{width: '0.5em'}} />
          <Tooltip title={t('縮小')}>
            <span>
              <LFCIconButton
                color="primary"
                size="small"
                onClick={() => handleZoomOut()}
                disabled={zoomPercent <= 80}
              >
                <RemoveIcon />
              </LFCIconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('リセット')}>
            <Typography
              variant="body1"
              sx={{textAlign: 'center', mx: 1, fontWeight: 'bold', cursor: 'pointer'}}
              onClick={handleResetZoom}
            >
              {zoomPercent}%
            </Typography>
          </Tooltip>
          <Tooltip title={t('拡大')}>
            <span>
              <LFCIconButton
                color="primary"
                size="small"
                onClick={() => handleZoomIn()}
                disabled={zoomPercent >= 120}
              >
                <AddIcon />
              </LFCIconButton>
            </span>
          </Tooltip>
          <Box sx={{width: '1em'}} />
          {fileName && (
            <Tooltip title={t('ダウンロード')}>
              <span>
                <LFCIconButton color="primary" size="small" onClick={() => handleDownload()}>
                  <DownloadIcon />
                </LFCIconButton>
              </span>
            </Tooltip>
          )}
          {hasHistory && onOpenHistory && (
            <Tooltip title={t('変更履歴')}>
              <span>
                <LFCIconButton color="primary" size="small" onClick={() => onOpenHistory()}>
                  <HistoryIcon />
                </LFCIconButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title={aiOpen ? t('AI解説を閉じる') : t('AI解説')}>
            <span>
              <LFCIconButton
                color={aiOpen ? 'secondary' : 'primary'}
                size="small"
                onClick={handleAiExplain}
                disabled={aiLoading}
              >
                {aiLoading ? <CircularProgress size={18} /> : <AutoStoriesIcon />}
              </LFCIconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* メインエリア: コードエディタ + AI解説パネル（横並び） */}
      <Box sx={{display: 'flex', flex: 1, overflow: 'hidden'}}>
        {/* コードエディタ領域 */}
        <Box
          ref={editorAreaRef}
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: editorSplit === 'vertical' ? 'column' : 'row',
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              overflow: 'hidden',
              display: 'flex',
              ...(editorSplit === 'none'
                ? {flex: 1}
                : editorSplit === 'horizontal'
                  ? {width: `${splitRatio}%`}
                  : {height: `${splitRatio}%`}),
              minWidth: 0,
              minHeight: 0
            }}
          >
            <CodePane {...codePaneProps} />
          </Box>
          {editorSplit !== 'none' && (
            <>
              {/* 分割バー */}
              <Box
                onMouseDown={handleSplitResizeStart}
                sx={
                  editorSplit === 'horizontal'
                    ? {
                        width: 6,
                        flexShrink: 0,
                        bgcolor: isDark ? 'grey.700' : 'grey.300',
                        '&:hover': {bgcolor: 'primary.main', opacity: 0.5},
                        transition: 'background-color 0.15s',
                        cursor: 'col-resize'
                      }
                    : {
                        height: 6,
                        flexShrink: 0,
                        bgcolor: isDark ? 'grey.700' : 'grey.300',
                        '&:hover': {bgcolor: 'primary.main', opacity: 0.5},
                        transition: 'background-color 0.15s',
                        cursor: 'row-resize'
                      }
                }
              />
              <Box
                sx={{
                  overflow: 'hidden',
                  display: 'flex',
                  flex: 1,
                  minWidth: 0,
                  minHeight: 0
                }}
              >
                <CodePane {...codePaneProps} />
              </Box>
            </>
          )}
        </Box>

        {/* AI解説パネル（右側固定） */}
        {aiOpen && (
          <>
            {/* リサイズハンドル */}
            <Box
              onMouseDown={handleAiResizeStart}
              sx={{
                width: 6,
                cursor: 'col-resize',
                bgcolor: isDark ? 'grey.700' : 'grey.300',
                '&:hover': {bgcolor: 'primary.main', opacity: 0.5},
                transition: 'background-color 0.15s',
                flexShrink: 0
              }}
            />
            <Box
              sx={{
                width: aiWidth,
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                borderLeft: 1,
                borderColor: 'divider',
                bgcolor: isDark ? alpha('#1a237e', 0.15) : alpha('#e3f2fd', 0.5)
              }}
            >
              {/* AI解説コンテンツ */}
              {aiError ? (
                <Alert severity={aiError === 'input-too-large' ? 'warning' : 'error'} sx={{m: 2}}>
                  {aiError === 'input-too-large'
                    ? t('AI解説の対象ファイルが大きすぎます。範囲を絞るか、ファイルを分割してください。')
                    : t('AI解説の取得に失敗しました。')}
                </Alert>
              ) : (
                <AiMarkdownViewer content={aiExplanation || ''} loading={aiLoading} />
              )}
            </Box>
          </>
        )}
      </Box>
    </Paper>
  );
};

export default SourceViewer;
